/**
 * AddTileWizard — 3-step "Add a button" flow (founder spec, P1 2026-05-24).
 *
 *   Step 1 — Type the word       (label + emoji + category + color)
 *   Step 2 — Add a picture       (camera, upload, or AI; SKIPPABLE)
 *   Step 3 — Record voice        (mic up to 5s; SKIPPABLE)
 *
 * Designed to replace the dense TileEditor modal for NEW tiles. Edits to
 * existing tiles still open TileEditor (power-user surface — gestalt,
 * time_scope, etc.).
 *
 * Steps 2 and 3 are explicitly skippable — the user can Save & Add Another
 * or Save & Done at any point after Step 1.
 */
import { useEffect, useState } from "react";
import {
  X, ArrowLeft, ArrowRight, Check, BookOpen, Image as ImageIcon,
  Mic, Loader2, Sparkles, Trash2,
} from "lucide-react";
import EmojiPicker from "./EmojiPicker";
import AudioRecorder from "./AudioRecorder";
import ImageUpload from "./ImageUpload";
import VocabPicker from "./VocabPicker";
import VoiceInput from "./VoiceInput";
import { ttsLangFor } from "@/lib/i18n";

const COLOR_OPTIONS = [
  "#FFB627", "#FF6B47", "#FF8A5B", "#FFD23F",
  "#6FAE6D", "#5AA9E6", "#F38BA8", "#C58A6B",
  "#FFFFFF", "#FBEFD9",
];

const STEPS = [
  { n: 1, label: "Word", icon: BookOpen },
  { n: 2, label: "Picture", icon: ImageIcon },
  { n: 3, label: "Voice", icon: Mic },
];

// Draft persistence (defect #2, 2026-07-04): autosave the in-progress
// tile draft to localStorage keyed per profile so back-navigating or
// tab-closing doesn't lose the parent's work.
const DRAFT_KEY = (pid) => `sonspeak_add_tile_draft_${pid || "no-profile"}`;
function loadTileDraft(pid) {
  try {
    const raw = localStorage.getItem(DRAFT_KEY(pid));
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (typeof d !== "object" || !d) return null;
    // TTL: 7 days. Old drafts are discarded silently.
    if (d.ts && Date.now() - d.ts > 7 * 24 * 3600 * 1000) return null;
    return d;
  } catch { return null; }
}
function saveTileDraft(pid, d) {
  try { localStorage.setItem(DRAFT_KEY(pid), JSON.stringify({ ...d, ts: Date.now() })); } catch { /* noop */ }
}
function clearTileDraft(pid) {
  try { localStorage.removeItem(DRAFT_KEY(pid)); } catch { /* noop */ }
}

export default function AddTileWizard({
  open, categories, onClose, onSave, lang = "en", profileId, aiEnabled = false,
}) {
  const [step, setStep] = useState(1);
  const [label, setLabel] = useState("");
  const [emoji, setEmoji] = useState("🔤");
  const [categoryId, setCategoryId] = useState("");
  const [color, setColor] = useState(COLOR_OPTIONS[0]);
  const [customImage, setCustomImage] = useState(null);
  const [customAudio, setCustomAudio] = useState(null);
  const [showVocab, setShowVocab] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [restored, setRestored] = useState(false);

  // Reset on every open. If a draft exists, restore it and show a banner.
  useEffect(() => {
    if (!open) return;
    const d = loadTileDraft(profileId);
    if (d && d.label) {
      setStep(d.step || 1);
      setLabel(d.label || "");
      setEmoji(d.emoji || "🔤");
      setCategoryId(d.categoryId || categories?.[0]?.id || "");
      setColor(d.color || (categories?.[0]?.color || COLOR_OPTIONS[0]));
      setCustomImage(d.customImage || null);
      setCustomAudio(d.customAudio || null);
      setError("");
      setDirty(false);
      setRestored(true);
    } else {
      setStep(1);
      setLabel("");
      setEmoji("🔤");
      setCategoryId(categories?.[0]?.id || "");
      setColor(categories?.[0]?.color || COLOR_OPTIONS[0]);
      setCustomImage(null);
      setCustomAudio(null);
      setError("");
      setDirty(false);
      setRestored(false);
    }
  }, [open, categories, profileId]);

  // Debounced autosave.
  useEffect(() => {
    if (!open) return undefined;
    if (!label && !customImage && !customAudio) return undefined;
    const h = setTimeout(() => {
      saveTileDraft(profileId, { step, label, emoji, categoryId, color, customImage, customAudio });
      setDirty(false);
    }, 400);
    return () => clearTimeout(h);
  }, [open, profileId, step, label, emoji, categoryId, color, customImage, customAudio]);

  // Mark dirty on user-visible input changes so the Save button's "unsaved"
  // indicator can appear immediately (before the debounce writes to LS).
  const markDirty = () => setDirty(true);

  const discardDraft = () => {
    clearTileDraft(profileId);
    setStep(1);
    setLabel("");
    setEmoji("🔤");
    setCategoryId(categories?.[0]?.id || "");
    setColor(categories?.[0]?.color || COLOR_OPTIONS[0]);
    setCustomImage(null);
    setCustomAudio(null);
    setError("");
    setDirty(false);
    setRestored(false);
  };

  if (!open) return null;

  const canAdvanceFromStep1 = label.trim().length > 0 && !!categoryId;

  const persist = async ({ closeAfter }) => {
    if (!canAdvanceFromStep1) {
      setError("Type the word and pick a category to continue.");
      setStep(1);
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onSave({
        label: label.trim(),
        speak_text: null,
        emoji,
        image_url: null,
        color,
        category_id: categoryId,
        is_favorite: false,
        is_gestalt: false,
        time_scope: "anytime",
        order: 0,
        custom_audio_b64: customAudio || null,
        custom_image_b64: customImage || null,
      });
      // Draft persisted successfully → clear it.
      clearTileDraft(profileId);
      setDirty(false);
      setRestored(false);
      if (closeAfter) {
        onClose();
      } else {
        // Save & Add another — reset, return to Step 1
        setStep(1);
        setLabel("");
        setEmoji("🔤");
        setCustomImage(null);
        setCustomAudio(null);
      }
    } catch (e) {
      setError(e?.message || "Could not save tile.");
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (step === 1 && !canAdvanceFromStep1) {
      setError("Type the word and pick a category to continue.");
      return;
    }
    setError("");
    setStep((s) => Math.min(3, s + 1));
  };
  const prev = () => { setError(""); setStep((s) => Math.max(1, s - 1)); };

  return (
    <div className="modal-overlay" data-testid="add-tile-wizard">
      <div className="brutal-card p-5 sm:p-6 w-full max-w-lg max-h-[92vh] overflow-y-auto bg-white">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <h2 className="display text-xl sm:text-2xl font-extrabold" data-testid="add-tile-title">
            Add a button — Step {step} of 3
          </h2>
          <button type="button" className="pill-btn" onClick={onClose} aria-label="Close" data-testid="add-tile-close">
            <X size={18} />
          </button>
        </div>

        {/* Restored-draft banner (defect #2). Sits above the step chip
            row so the parent immediately sees why old values reappeared. */}
        {restored && (
          <div
            className="brutal-card bg-[color:var(--gold)] p-2 mb-3 text-xs flex items-center justify-between gap-2"
            data-testid="add-tile-restored-banner"
          >
            <span>
              <strong>Restored your unsaved work.</strong> Your last draft was still here.
            </span>
            <button
              type="button"
              onClick={discardDraft}
              className="pill-btn"
              data-testid="add-tile-discard-draft"
            >
              <Trash2 size={12} /> Discard
            </button>
          </div>
        )}

        {/* Step indicator */}
        <ol className="flex items-center gap-1 mb-4" data-testid="add-tile-steps">
          {STEPS.map((s) => {
            const done = step > s.n;
            const active = step === s.n;
            return (
              <li
                key={s.n}
                className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-md ${
                  active ? "bg-[color:var(--gold)]" : done ? "bg-[color:var(--cream)]" : "bg-[color:var(--paper)]"
                }`}
                data-testid={`add-tile-step-${s.n}`}
                aria-current={active ? "step" : undefined}
              >
                {done ? <Check size={12} aria-hidden /> : <s.icon size={12} aria-hidden />}
                <span>{s.n}. {s.label}</span>
              </li>
            );
          })}
        </ol>

        {error && (
          <div
            className="brutal-card bg-red-50 p-3 text-sm text-red-700 mb-3"
            data-testid="add-tile-error"
          >
            {error}
          </div>
        )}

        {/* ---------------- STEP 1 — Word ----------------- */}
        {step === 1 && (
          <div data-testid="add-tile-step-1-pane">
            <label className="block mb-3">
              <span className="display font-semibold mb-1 block">Word or phrase</span>
              <input
                className="brutal-input w-full"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Goldfish crackers, Hi Grandma!"
                maxLength={120}
                required
                autoFocus
                data-testid="add-tile-label"
              />
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <VoiceInput
                  lang={ttsLangFor(lang)}
                  onResult={(text) => setLabel(text)}
                  data-testid="add-tile-voice-input"
                />
                <button
                  type="button"
                  className="pill-btn gold"
                  onClick={() => setShowVocab(true)}
                  data-testid="add-tile-browse-vocab"
                >
                  <BookOpen size={14} /> Browse 500+ words
                </button>
              </div>
            </label>

            <div className="mb-3">
              <span className="display font-semibold mb-1 block">Symbol</span>
              <EmojiPicker value={emoji} onChange={setEmoji} />
            </div>

            <label className="block mb-3">
              <span className="display font-semibold mb-1 block">Category</span>
              <select
                className="brutal-input w-full"
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  const cat = categories.find((c) => c.id === e.target.value);
                  if (cat?.color) setColor(cat.color);
                }}
                required
                data-testid="add-tile-category"
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.emoji}  {c.label || c.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="mb-2">
              <span className="display font-semibold mb-1 block">Tile color</span>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`w-9 h-9 rounded-full border-[3px] border-[color:var(--ink)] ${color === c ? "ring-4 ring-[color:var(--gold)]" : ""}`}
                    style={{ background: c }}
                    aria-label={`Color ${c}`}
                    data-testid={`add-tile-color-${c}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ---------------- STEP 2 — Picture ----------------- */}
        {step === 2 && (
          <div data-testid="add-tile-step-2-pane">
            <div className="brutal-card p-3 bg-[color:var(--gold)] mb-3">
              <div className="display font-extrabold mb-1 flex items-center gap-2">
                <ImageIcon size={16} aria-hidden /> Add a real photo
                <span className="text-[10px] uppercase tracking-[0.2em] bg-[color:var(--ink)] text-white px-1.5 py-0.5 rounded font-bold">
                  recommended
                </span>
              </div>
              <p className="text-sm text-[color:var(--ink)] mb-2 leading-snug">
                Real faces and objects make recognition jump. Use the camera,
                pick a saved photo, or let AI generate one.
              </p>
              <p className="text-[11px] text-[color:var(--ink)]/70 mb-2 leading-snug">
                <strong>Privacy:</strong> photos are saved only on this device.
                They never leave it.
              </p>
              <ImageUpload
                value={customImage}
                onChange={(v) => { setCustomImage(v); markDirty(); }}
                aiLabel={label}
                aiEnabled={aiEnabled}
                profileId={profileId}
              />
            </div>
            <button
              type="button"
              onClick={next}
              className="text-xs text-[color:var(--ink)]/60 underline"
              data-testid="add-tile-skip-picture"
            >
              <Sparkles size={12} className="inline -mt-0.5" aria-hidden /> Skip picture — I'll add it later
            </button>
          </div>
        )}

        {/* ---------------- STEP 3 — Voice ----------------- */}
        {step === 3 && (
          <div data-testid="add-tile-step-3-pane">
            <div className="brutal-card p-3 bg-[color:var(--paper-muted)] mb-3">
              <div className="display font-extrabold mb-1 flex items-center gap-2">
                <Mic size={16} aria-hidden /> Your voice (optional)
              </div>
              <p className="text-sm text-[color:var(--ink-soft)] mb-2 leading-snug">
                Record yourself, a parent, or your child saying this word. It
                plays instead of the built-in voice.
              </p>
              <p className="text-[11px] text-[color:var(--ink-soft)] mb-2 leading-snug">
                <strong>Privacy:</strong> voice recordings are saved only on
                this device. They never leave it.
              </p>
              <AudioRecorder
                value={customAudio}
                onChange={setCustomAudio}
                profileId={profileId}
              />
            </div>
            <p className="text-xs text-[color:var(--ink)]/60">
              No recording? Save anyway — the built-in voice will speak this tile.
            </p>
          </div>
        )}

        {/* Footer / nav */}
        <div className="flex items-center gap-2 justify-between mt-5 pt-4 border-t border-[color:var(--ink)]/10">
          {/* Save-state indicator — sits next to Back so parents always see
              whether their in-progress draft is safe. */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={prev}
              disabled={step === 1 || busy}
              className="pill-btn"
              data-testid="add-tile-prev"
            >
              <ArrowLeft size={14} /> Back
            </button>
            <span
              className={`text-[11px] font-semibold ${dirty ? "text-[color:var(--coral-deep)]" : "text-[color:var(--ink)]/50"}`}
              data-testid="add-tile-draft-state"
              aria-live="polite"
            >
              {label || customImage || customAudio ? (dirty ? "● Unsaved" : "✓ Draft saved") : ""}
            </span>
          </div>
          <div className="flex gap-2">
            {step < 3 ? (
              <button
                type="button"
                onClick={next}
                disabled={busy || (step === 1 && !canAdvanceFromStep1)}
                className="pill-btn primary"
                data-testid="add-tile-next"
              >
                Next <ArrowRight size={14} />
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => persist({ closeAfter: false })}
              disabled={busy || !canAdvanceFromStep1}
              className="pill-btn"
              data-testid="add-tile-save-another"
              title="Save and add another button"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Save & add another
            </button>
            <button
              type="button"
              onClick={() => persist({ closeAfter: true })}
              disabled={busy || !canAdvanceFromStep1}
              className="pill-btn primary"
              data-testid="add-tile-save-done"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Save & done
            </button>
          </div>
        </div>
      </div>

      <VocabPicker
        open={showVocab}
        onClose={() => setShowVocab(false)}
        onPick={(w) => {
          setLabel(w.label);
          if (w.emoji) setEmoji(w.emoji);
          setShowVocab(false);
        }}
      />
    </div>
  );
}
